import React, { useState, useEffect, useRef, Fragment } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Badge, Button, ButtonGroup, Input, Label } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import axios from 'axios';
import API_BASE_URL from '../../../../Config/api';
import { statusColor } from '../../../../Config/orderStatuses';
import { toast } from 'react-toastify';
import authHeader from '../../../Customer/authHeader';

// Helper to get the Monday of the current week
const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
};

// Format date as YYYY-MM-DD
const formatDate = (d) => {
  const date = new Date(d);
  return date.toISOString().split('T')[0];
};

// Format date for display
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Date and time an order entered its current status (orders.status_changed_at)
const formatDisplayDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

// Format currency
const CURRENCY_SYMBOLS = { USD: '$', GBP: '\u00A3', EUR: '\u20AC' };

const formatCurrency = (amount, currency) => {
  if (!amount) return '-';
  const num = parseFloat(amount);
  return `${CURRENCY_SYMBOLS[currency] || '\u20AC'}${num.toFixed(2)}`;
};

// {EUR: 10, USD: 5} -> "\u20AC10.00, $5.00" (orders can be in different currencies)
const formatTotals = (totals) => {
  const parts = Object.entries(totals || {}).map(([cur, amt]) => `${CURRENCY_SYMBOLS[cur] || '\u20AC'}${parseFloat(amt).toFixed(2)}`);
  return parts.length ? parts.join(', ') : '-';
};

// Generate list of Mondays for the dropdown (past 52 weeks + next 4 weeks)
const generateWeekOptions = () => {
  const weeks = [];
  const today = new Date();
  const currentMonday = getMonday(today);

  // 4 weeks into the future
  for (let i = -4; i <= 52; i++) {
    const monday = new Date(currentMonday);
    monday.setDate(monday.getDate() - (i * 7));
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const prevMonday = new Date(monday);
    prevMonday.setDate(prevMonday.getDate() - 7);
    const prevSunday = new Date(monday);
    prevSunday.setDate(prevSunday.getDate() - 1);

    weeks.push({
      reportDate: formatDate(monday),
      startDate: formatDate(prevMonday),
      endDate: formatDate(prevSunday),
      label: `${formatDisplayDate(monday)} - Week of ${formatDisplayDate(prevMonday)} to ${formatDisplayDate(prevSunday)}`
    });
  }
  return weeks;
};

// Generate list of months for the dropdown (current month + previous 24).
// Built from plain year/month numbers rather than Date -> ISO string so the
// range can't shift a day with the browser's timezone.
const generateMonthOptions = () => {
  const months = [];
  const today = new Date();
  for (let i = 0; i <= 24; i++) {
    const first = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = first.getFullYear();
    const month = first.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const mm = String(month + 1).padStart(2, '0');
    const name = first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    months.push({
      startDate: `${year}-${mm}-01`,
      endDate: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
      label: i === 0 ? `${name} (month to date)` : name
    });
  }
  return months;
};

const WeeklyReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weekOptions] = useState(generateWeekOptions());
  const [selectedWeek, setSelectedWeek] = useState('');
  const [reportType, setReportType] = useState('weekly');
  const [monthOptions] = useState(generateMonthOptions());
  const [selectedMonth, setSelectedMonth] = useState(JSON.stringify(generateMonthOptions()[0]));
  const [exporting, setExporting] = useState(false);
  const latestRequest = useRef(0);
  const isMonthly = reportType === 'monthly';
  const periodWord = isMonthly ? 'month' : 'week';

  // Auto-select current week on mount
  useEffect(() => {
    if (weekOptions.length > 0) {
      // Find the current or most recent Monday
      const today = new Date();
      const currentMonday = getMonday(today);
      const mondayStr = formatDate(currentMonday);
      const match = weekOptions.find(w => w.reportDate === mondayStr);
      if (match) {
        setSelectedWeek(JSON.stringify(match));
      } else {
        setSelectedWeek(JSON.stringify(weekOptions[4])); // default to current week
      }
    }
  }, [weekOptions]);

  const selectedPeriod = isMonthly ? selectedMonth : selectedWeek;

  const fetchReport = async () => {
    if (!selectedPeriod) return;
    const period = JSON.parse(selectedPeriod);
    // Ignore a slow response for a period the user has already switched away from
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/weekly-report`, {
        params: { startDate: period.startDate, endDate: period.endDate }
      });
      if (requestId === latestRequest.current) setReportData(response.data);
    } catch (err) {
      console.error(`Error fetching ${reportType} report:`, err);
      if (requestId === latestRequest.current) toast.error(`Failed to fetch ${reportType} report`);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  // Auto-fetch when the report type or the selected week/month changes
  useEffect(() => {
    if (selectedPeriod) {
      setReportData(null);
      fetchReport();
    }
  }, [selectedPeriod, reportType]);

  const exportReport = async () => {
    if (!selectedPeriod) return;
    const period = JSON.parse(selectedPeriod);
    setExporting(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/weekly-report/export`, {
        ...authHeader(),
        params: { startDate: period.startDate, endDate: period.endDate, type: reportType },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fara-${reportType}-report-${period.startDate}_to_${period.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
      toast.error('Failed to download report');
    } finally {
      setExporting(false);
    }
  };

  const selectedWeekData = selectedPeriod ? JSON.parse(selectedPeriod) : null;

  return (
    <Fragment>
      <Breadcrumbs parent="Reports" title={isMonthly ? 'Monthly Report' : 'Weekly Report'} />
      <Container fluid={true}>
        {/* Week Selector */}
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="pb-0">
                <Row className="align-items-center">
                  <Col>
                    <h5>{isMonthly ? 'Select Report Month' : 'Select Report Week'}</h5>
                    <p className="text-muted mb-0">
                      {isMonthly
                        ? 'Choose a month to view the report for that full calendar month'
                        : 'Choose a Monday to view the report for the preceding week (Monday to Sunday)'}
                    </p>
                  </Col>
                  <Col xs="auto">
                    <ButtonGroup>
                      <Button color="primary" outline={isMonthly} active={!isMonthly} onClick={() => setReportType('weekly')}>
                        Weekly
                      </Button>
                      <Button color="primary" outline={!isMonthly} active={isMonthly} onClick={() => setReportType('monthly')}>
                        Monthly
                      </Button>
                    </ButtonGroup>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Row className="align-items-end">
                  <Col md="8">
                    <Label className="form-label">{isMonthly ? 'Report Month' : 'Report Week'}</Label>
                    {isMonthly ? (
                      <Input
                        type="select"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      >
                        {monthOptions.map((month, idx) => (
                          <option key={idx} value={JSON.stringify(month)}>
                            {month.label}
                          </option>
                        ))}
                      </Input>
                    ) : (
                      <Input
                        type="select"
                        value={selectedWeek}
                        onChange={(e) => setSelectedWeek(e.target.value)}
                      >
                        <option value="">Select a week...</option>
                        {weekOptions.map((week, idx) => (
                          <option key={idx} value={JSON.stringify(week)}>
                            {week.label}
                          </option>
                        ))}
                      </Input>
                    )}
                  </Col>
                  <Col md="4">
                    <Button color="primary" onClick={fetchReport} disabled={loading || !selectedPeriod}>
                      {loading ? 'Loading...' : 'Refresh Report'}
                    </Button>
                    <Button color="primary" outline className="ms-2" onClick={exportReport} disabled={exporting || !selectedPeriod}>
                      {exporting ? 'Preparing...' : 'Download Excel'}
                    </Button>
                  </Col>
                </Row>
                {selectedWeekData && (
                  <div className="mt-3">
                    <span className="text-muted">
                      Showing data from <strong>{formatDisplayDate(selectedWeekData.startDate)}</strong> to <strong>{formatDisplayDate(selectedWeekData.endDate)}</strong>
                    </span>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Summary Cards */}
        {reportData && (
          <Row>
            <Col xl="2" lg="4" sm="6">
              <Card className="o-hidden">
                <CardBody className="b-r-4" style={{ backgroundColor: '#7366ff', padding: '20px' }}>
                  <div className="media static-top-widget">
                    <div className="media-body">
                      <span className="m-0" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>New Orders</span>
                      <h4 className="mb-0 counter" style={{ color: '#fff', fontSize: '28px', fontWeight: 600 }}>
                        {reportData.summary.newOrders}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="2" lg="4" sm="6">
              <Card className="o-hidden">
                <CardBody className="b-r-4" style={{ backgroundColor: '#f73164', padding: '20px' }}>
                  <div className="media static-top-widget">
                    <div className="media-body">
                      <span className="m-0" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Shipped</span>
                      <h4 className="mb-0 counter" style={{ color: '#fff', fontSize: '28px', fontWeight: 600 }}>
                        {reportData.summary.shipped}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="2" lg="4" sm="6">
              <Card className="o-hidden">
                <CardBody className="b-r-4" style={{ backgroundColor: '#ff9f40', padding: '20px' }}>
                  <div className="media static-top-widget">
                    <div className="media-body">
                      <span className="m-0" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>In Production</span>
                      <h4 className="mb-0 counter" style={{ color: '#fff', fontSize: '28px', fontWeight: 600 }}>
                        {reportData.summary.inProduction}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="2" lg="4" sm="6">
              <Card className="o-hidden">
                <CardBody className="b-r-4" style={{ backgroundColor: '#51bb25', padding: '20px' }}>
                  <div className="media static-top-widget">
                    <div className="media-body">
                      <span className="m-0" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Delivered</span>
                      <h4 className="mb-0 counter" style={{ color: '#fff', fontSize: '28px', fontWeight: 600 }}>
                        {reportData.summary.delivered}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="2" lg="4" sm="6">
              <Card className="o-hidden">
                <CardBody className="b-r-4" style={{ backgroundColor: '#36b7b5', padding: '20px' }}>
                  <div className="media static-top-widget">
                    <div className="media-body">
                      <span className="m-0" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Payments</span>
                      <h4 className="mb-0 counter" style={{ color: '#fff', fontSize: '28px', fontWeight: 600 }}>
                        {reportData.summary.paymentsReceived}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="2" lg="4" sm="6">
              <Card className="o-hidden">
                <CardBody className="b-r-4" style={{ backgroundColor: '#a927f9', padding: '20px' }}>
                  <div className="media static-top-widget">
                    <div className="media-body">
                      <span className="m-0" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Payment Total</span>
                      <h4 className="mb-0 counter" style={{ color: '#fff', fontSize: '22px', fontWeight: 600 }}>
                        {'\u20AC'}{reportData.summary.totalPaymentAmount.toFixed(2)}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="2" lg="4" sm="6">
              <Card className="o-hidden">
                <CardBody className="b-r-4" style={{ backgroundColor: '#3b5998', padding: '20px' }}>
                  <div className="media static-top-widget">
                    <div className="media-body">
                      <span className="m-0" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Credit Notes</span>
                      <h4 className="mb-0 counter" style={{ color: '#fff', fontSize: '28px', fontWeight: 600 }}>
                        {reportData.summary.creditNotes}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="2" lg="4" sm="6">
              <Card className="o-hidden">
                <CardBody className="b-r-4" style={{ backgroundColor: '#6c757d', padding: '20px' }}>
                  <div className="media static-top-widget">
                    <div className="media-body">
                      <span className="m-0" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Cancelled</span>
                      <h4 className="mb-0 counter" style={{ color: '#fff', fontSize: '28px', fontWeight: 600 }}>
                        {reportData.summary.cancelled}
                      </h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {loading && (
          <Row>
            <Col sm="12" className="text-center py-5">
              <div className="loader-box" style={{ display: 'inline-block' }}>
                <div className="loader-7" />
              </div>
              <p className="mt-2 text-muted">Loading report data...</p>
            </Col>
          </Row>
        )}

        {reportData && !loading && (
          <>
            {/* New Orders */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="primary" className="me-2">{reportData.summary.newOrders}</Badge>
                      New Orders
                    </h5>
                    <p className="text-muted mb-0">Orders entered into the system between {formatDisplayDate(reportData.period.startDate)} and {formatDisplayDate(reportData.period.endDate)}</p>
                    <p className="text-muted small">Counted by the day the order was entered. Order Date is the date on the order and can differ. Orders still waiting in New Order status, whenever they were entered, are listed under Orders Awaiting Action below.</p>
                  </CardHeader>
                  <CardBody>
                    {reportData.newOrders.length === 0 ? (
                      <p className="text-muted text-center py-3">No new orders this {periodWord}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Store</th>
                              <th>Entered</th>
                              <th>Order Date</th>
                              <th>Items</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th>Status Changed</th>
                              <th>Check</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.newOrders.map((order) => (
                              <tr key={order.order_id}>
                                <td><strong>{order.order_id}</strong></td>
                                <td>{order.store_name}</td>
                                <td>{formatDisplayDate(order.created_at)}</td>
                                <td>{order.data_issue ? '-' : formatDisplayDate(order.order_date)}</td>
                                <td>{order.item_count || 0}</td>
                                <td>{formatCurrency(order.total_amount, order.currency)}</td>
                                <td>
                                  <Badge color={getStatusColor(order.status)}>{order.status}</Badge>
                                </td>
                                <td>{formatDisplayDateTime(order.status_changed_at)}</td>
                                <td>
                                  {order.data_issue && <Badge color="warning" title={order.data_issue}>{order.data_issue}</Badge>}
                                  {!order.data_issue && order.date_note && <small className="text-muted">{order.date_note}</small>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Orders Shipped */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="danger" className="me-2">{reportData.summary.shipped}</Badge>
                      Orders Shipped
                    </h5>
                    <p className="text-muted">Orders shipped between {formatDisplayDate(reportData.period.startDate)} and {formatDisplayDate(reportData.period.endDate)}</p>
                  </CardHeader>
                  <CardBody>
                    {reportData.shippedOrders.length === 0 ? (
                      <p className="text-muted text-center py-3">No orders shipped this {periodWord}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Store</th>
                              <th>Order Date</th>
                              <th>Shipped Date</th>
                              <th>Items</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.shippedOrders.map((order) => (
                              <tr key={order.order_id}>
                                <td><strong>{order.order_id}</strong></td>
                                <td>{order.store_name}</td>
                                <td>{formatDisplayDate(order.order_date)}</td>
                                <td>{formatDisplayDate(order.shipped_date)}</td>
                                <td>{order.item_count || 0}</td>
                                <td>{formatCurrency(order.total_amount, order.currency)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Unconfirmed Orders (status New Order) */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="primary" className="me-2">{reportData.summary.unconfirmed}</Badge>
                      Unconfirmed Orders
                    </h5>
                    <p className="text-muted">Every order still in New Order status, however long ago it was entered (not limited to the selected {periodWord}). Oldest first.</p>
                  </CardHeader>
                  <CardBody>
                    {reportData.unconfirmedOrders.length === 0 ? (
                      <p className="text-muted text-center py-3">No orders are waiting in New Order status</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Store</th>
                              <th>Entered</th>
                              <th>Order Date</th>
                              <th>Waiting</th>
                              <th>Status Changed</th>
                              <th>Items</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.unconfirmedOrders.map((order) => (
                              <tr key={order.order_id}>
                                <td><strong>{order.order_id}</strong></td>
                                <td>{order.store_name}</td>
                                <td>{formatDisplayDate(order.created_at)}</td>
                                <td>{order.data_issue ? <Badge color="warning" title={order.data_issue}>{order.data_issue}</Badge> : formatDisplayDate(order.order_date)}</td>
                                <td>{order.entered_age_days === null ? '-' : `${order.entered_age_days} days`}</td>
                                <td>{formatDisplayDateTime(order.status_changed_at)}</td>
                                <td>{order.item_count || 0}</td>
                                <td>{formatCurrency(order.total_amount, order.currency)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Orders Awaiting Action (status Awaiting Action, with reason) */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="danger" className="me-2">{reportData.summary.awaitingAction}</Badge>
                      Orders Awaiting Action
                    </h5>
                    <p className="text-muted">Orders put on hold with a reason (Awaiting Action status), not limited to the selected {periodWord}. Longest waiting first.</p>
                  </CardHeader>
                  <CardBody>
                    {reportData.awaitingActionOrders.length === 0 ? (
                      <p className="text-muted text-center py-3">No orders are in Awaiting Action status</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Store</th>
                              <th>Awaiting Since</th>
                              <th>Days</th>
                              <th>Items</th>
                              <th>Amount</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.awaitingActionOrders.map((order) => (
                              <tr key={order.order_id}>
                                <td><strong>{order.order_id}</strong></td>
                                <td>{order.store_name}</td>
                                <td>{formatDisplayDateTime(order.status_changed_at)}</td>
                                <td>{order.status_age_days === null || order.status_age_days === undefined ? '-' : order.status_age_days}</td>
                                <td>{order.item_count || 0}</td>
                                <td>{formatCurrency(order.total_amount, order.currency)}</td>
                                <td>{order.awaiting_action_reason || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Orders In Production */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="warning" className="me-2">{reportData.summary.inProduction}</Badge>
                      Orders In Production
                    </h5>
                    <p className="text-muted mb-0">All orders currently in production status, with the amount still pending on each invoice</p>
                    <p className="mb-0 mt-1">
                      <strong>Total pending:</strong> {formatTotals(reportData.summary.pendingByCurrency)}
                    </p>
                  </CardHeader>
                  <CardBody>
                    {reportData.inProductionOrders.length === 0 ? (
                      <p className="text-muted text-center py-3">No orders in production</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Store</th>
                              <th>Order Date</th>
                              <th>In Production Since</th>
                              <th>Items</th>
                              <th>Invoice</th>
                              <th>Total</th>
                              <th>Paid</th>
                              <th>Pending</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.inProductionOrders.map((order) => (
                              <tr key={order.order_id}>
                                <td><strong>{order.order_id}</strong></td>
                                <td>{order.store_name}</td>
                                <td>{formatDisplayDate(order.order_date)}</td>
                                <td>{formatDisplayDateTime(order.status_changed_at)}{order.status_age_days !== null && order.status_age_days !== undefined && <small className="text-muted d-block">{order.status_age_days} days</small>}</td>
                                <td>{order.item_count || 0}</td>
                                <td>{order.invoice_id || <span className="text-muted">No invoice</span>}</td>
                                <td>{formatCurrency(order.total_amount, order.currency)}</td>
                                <td>{formatCurrency(order.total_paid, order.currency)}</td>
                                <td><strong>{order.pending_amount > 0 ? formatCurrency(order.pending_amount, order.currency) : 'Paid'}</strong></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Orders Delivered */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="success" className="me-2">{reportData.summary.delivered}</Badge>
                      Orders Delivered
                    </h5>
                    <p className="text-muted">Orders delivered between {formatDisplayDate(reportData.period.startDate)} and {formatDisplayDate(reportData.period.endDate)}</p>
                  </CardHeader>
                  <CardBody>
                    {reportData.deliveredOrders.length === 0 ? (
                      <p className="text-muted text-center py-3">No orders delivered this {periodWord}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Store</th>
                              <th>Order Date</th>
                              <th>Delivered Date</th>
                              <th>Items</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.deliveredOrders.map((order) => (
                              <tr key={order.order_id}>
                                <td><strong>{order.order_id}</strong></td>
                                <td>{order.store_name}</td>
                                <td>{formatDisplayDate(order.order_date)}</td>
                                <td>{formatDisplayDate(order.delivered_date)}</td>
                                <td>{order.item_count || 0}</td>
                                <td>{formatCurrency(order.total_amount, order.currency)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Payments Received */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="info" className="me-2">{reportData.summary.paymentsReceived}</Badge>
                      Payments Received
                    </h5>
                    <p className="text-muted">Payments received between {formatDisplayDate(reportData.period.startDate)} and {formatDisplayDate(reportData.period.endDate)}</p>
                  </CardHeader>
                  <CardBody>
                    {reportData.paymentsReceived.length === 0 ? (
                      <p className="text-muted text-center py-3">No payments received this {periodWord}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Invoice ID</th>
                              <th>Store</th>
                              <th>Payment Date</th>
                              <th>Payment Type</th>
                              <th>Amount</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.paymentsReceived.map((payment) => (
                              <tr key={payment.payment_id}>
                                <td><strong>{payment.order_id}</strong></td>
                                <td>{payment.invoice_id}</td>
                                <td>{payment.store_name}</td>
                                <td>{formatDisplayDate(payment.payment_date)}</td>
                                <td>
                                  <Badge color={payment.payment_type === 'Full Payment' ? 'success' : 'warning'}>
                                    {payment.payment_type || '-'}
                                  </Badge>
                                </td>
                                <td>{formatCurrency(payment.payment_amount, payment.currency)}</td>
                                <td>{payment.notes || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Credit Notes Generated */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="primary" className="me-2">{reportData.summary.creditNotes}</Badge>
                      Credit Notes Generated
                    </h5>
                    <p className="text-muted">Credit notes created between {formatDisplayDate(reportData.period.startDate)} and {formatDisplayDate(reportData.period.endDate)}</p>
                  </CardHeader>
                  <CardBody>
                    {reportData.creditNotes.length === 0 ? (
                      <p className="text-muted text-center py-3">No credit notes generated this {periodWord}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Credit Note</th>
                              <th>Store</th>
                              <th>Order ID</th>
                              <th>Date</th>
                              <th>Shipping</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.creditNotes.map((cn) => (
                              <tr key={cn.id}>
                                <td><strong>{cn.credit_note_number || `CN-${cn.id}`}</strong></td>
                                <td>{cn.store_name}</td>
                                <td>{cn.original_order_id || '-'}</td>
                                <td>{formatDisplayDate(cn.created_at)}</td>
                                <td>{formatCurrency(cn.shipping_amount, cn.currency)}</td>
                                <td>{formatCurrency(cn.amount, cn.currency)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Orders Cancelled */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="danger" className="me-2">{reportData.summary.cancelled}</Badge>
                      Orders Cancelled
                    </h5>
                    <p className="text-muted">Orders cancelled between {formatDisplayDate(reportData.period.startDate)} and {formatDisplayDate(reportData.period.endDate)}</p>
                    {reportData.summary.cancelledUndated > 0 && (
                      <p className="text-muted small">
                        {reportData.summary.cancelledUndated} older cancelled order(s) have no recorded cancellation date, so they can't be placed in a {periodWord} and aren't listed here. They are counted in the Status Breakdown below.
                      </p>
                    )}
                  </CardHeader>
                  <CardBody>
                    {reportData.cancelledOrders.length === 0 ? (
                      <p className="text-muted text-center py-3">No orders cancelled this {periodWord}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table-border-horizontal">
                          <thead>
                            <tr>
                              <th>Order ID</th>
                              <th>Store</th>
                              <th>Order Date</th>
                              <th>Cancelled Date</th>
                              <th>Items</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.cancelledOrders.map((order) => (
                              <tr key={order.order_id}>
                                <td><strong>{order.order_id}</strong></td>
                                <td>{order.store_name}</td>
                                <td>{formatDisplayDate(order.order_date)}</td>
                                <td>{formatDisplayDate(order.cancelled_date)}</td>
                                <td>{order.item_count || 0}</td>
                                <td>{formatCurrency(order.total_amount, order.currency)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Status Breakdown */}
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader className="pb-0">
                    <h5>
                      <Badge color="secondary" className="me-2">{reportData.summary.totalOrders}</Badge>
                      Status Breakdown
                    </h5>
                    <p className="text-muted">Current status of all orders (a snapshot as of now, not limited to the selected {periodWord})</p>
                  </CardHeader>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table-border-horizontal">
                        <thead>
                          <tr>
                            <th>Status</th>
                            <th>Orders</th>
                            <th>Value</th>
                            <th>Latest Change</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.statusBreakdown.map((row) => (
                            <tr key={row.status}>
                              <td><Badge color={getStatusColor(row.status)}>{row.status}</Badge></td>
                              <td><strong>{row.count}</strong></td>
                              <td>{formatTotals(row.totals)}</td>
                              <td>{formatDisplayDateTime(row.latest_change)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </Fragment>
  );
};

// Badge colour comes from the shared status list (GET /api/order-statuses)
const getStatusColor = (status) => statusColor(status);

export default WeeklyReport;
